import './polyfills';

import { enableProdMode, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { isProdMode, versionInfo, amBaseUrl } from './config';
import { apiProviders } from './api';
import { serviceProviders } from './services';
import { HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http';
import { ErrorInterceptor } from './services/interceptors/error.interceptor';
import { ServerErrorInterceptor } from './services/interceptors/server-error.interceptor';
import { ServerErrorGuard } from './services/guards/server-error.guard';
import { Routes, withHashLocation, provideRouter } from '@angular/router';
import { PagesComponent } from './pages/pages.component';
import {
  AppGuard,
  TextInputModule,
  ShellV2Module,
  ActionMenuModule,
  ButtonV2Module,
  ButtonModule,
  CheckboxV2Module,
  ModalModule,
  SideDrawerModule,
  AssetsModule,
  UnitsModule,
  FileUploadV2Module,
  PagesV2Module,
  ChipV2Module,
  IconV2Module,
  ToggleV2Module,
  InputV2Module,
  SelectV2Module,
  RadioV2Module,
  GridV2Module,
  SpinnerModule,
  OutputV2Module,
  NotificationsV2Module,
  DateV2Module,
  UserRightsModule,
  PipesModule,
  WorkflowModule,
  MultipleTranslatePoHttpLoader,
} from '@schenck/rotec-components';
import { routes } from './routes';
import { TranslateLoader, provideTranslateService } from '@ngx-translate/core';
import { StoreModule } from '@ngrx/store';
import { RotorTypeDetailsStoreName } from './pages/rotor-type-details/store/rotor-type-details-store.basic';
import { rotorTypeDetailsReducer } from './pages/rotor-type-details/store/rotor-type-details-store.reducer';
import {
  RotorTypeListStoreName,
  rotorTypeListReducer,
} from './pages/rotor-type-list/store/rotor-type-list.reducers';
import { EffectsModule } from '@ngrx/effects';
import { RotorTypeListEffects } from './pages/rotor-type-list/store/rotor-type-list.effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { environment } from './environment/environment';
import { provideAnimations } from '@angular/platform-browser/animations';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { ModalsModule } from './components/modals/modals.module';
import { ReactiveFormsModule } from '@angular/forms';
import { MatRadioModule } from '@angular/material/radio';
import { AppComponent } from './app.component';
import { bootstrapApplication } from '@angular/platform-browser';

import deLocale from '@angular/common/locales/de';
import enLocale from '@angular/common/locales/en';
import esLocale from '@angular/common/locales/es';
import frLocale from '@angular/common/locales/fr';
import itLocale from '@angular/common/locales/it';
import nlLocale from '@angular/common/locales/nl';
import svLocale from '@angular/common/locales/sv';
import plLocale from '@angular/common/locales/pl';
import trLocale from '@angular/common/locales/tr';
import zhLocale from '@angular/common/locales/zh';
import huLocale from '@angular/common/locales/hu';
import arLocale from '@angular/common/locales/ar';
import csLocale from '@angular/common/locales/cs';
import koLocale from '@angular/common/locales/ko';
import jaLocale from '@angular/common/locales/ja';
import ptLocale from '@angular/common/locales/pt';
import { registerLocaleData } from '@angular/common';

registerLocaleData(deLocale, 'de');
registerLocaleData(enLocale, 'en');
registerLocaleData(esLocale, 'es');
registerLocaleData(frLocale, 'fr');
registerLocaleData(itLocale, 'it');
registerLocaleData(nlLocale, 'nl');
registerLocaleData(svLocale, 'sv');
registerLocaleData(plLocale, 'pl');
registerLocaleData(trLocale, 'tr');
registerLocaleData(zhLocale, 'zh');
registerLocaleData(huLocale, 'hu');
registerLocaleData(arLocale, 'ar');
registerLocaleData(csLocale, 'cs');
registerLocaleData(koLocale, 'ko');
registerLocaleData(jaLocale, 'ja');
registerLocaleData(ptLocale, 'pt');

function HttpPoLoaderFactory(http: HttpClient) {
  return new MultipleTranslatePoHttpLoader(http, [
    { prefix: 'assets/rotec-locales/', suffix: '.po' },
    { prefix: 'assets/locales/', suffix: '.po' },
  ]);
}

const appRoutes: Routes = [
  {
    path: '',
    component: PagesComponent,
    canActivate: [AppGuard],
    children: [
      {
        path: routes.rotorTypeList.url,
        loadComponent: () =>
          import('./pages/rotor-type-list/rotor-type-list.component').then(
            (m) => m.RotorTypeListComponent
          ),
      },
      {
        path: routes.rotorTypeDetailsCreate.url,
        loadComponent: () =>
          import('./pages/rotor-type-details/rotor-type-details.component').then(
            (m) => m.RotorTypeDetailsComponent
          ),
        data: {
          title: 'Create',
        },
      },
      {
        path: `${routes.rotorTypeDetailsEdit.url}/:id`,
        loadComponent: () =>
          import('./pages/rotor-type-details/rotor-type-details.component').then(
            (m) => m.RotorTypeDetailsComponent
          ),
        data: {
          title: 'Edit',
        },
      },
      {
        path: routes.unhandledError.url,
        loadComponent: () =>
          import('./components/service-unavailable/service-unavailable.component').then(
            (m) => m.ServiceUnavailableComponent
          ),
        canActivate: [ServerErrorGuard],
      },
      {
        path: '',
        redirectTo: routes.rotorTypeList.url,
        pathMatch: 'full',
      },
    ],
  },
  { path: '**', redirectTo: routes.rotorTypeList.url },
];

if (isProdMode) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpPoLoaderFactory,
        deps: [HttpClient],
      },
    }),
    importProvidersFrom(
      TextInputModule,
      StoreModule.forRoot({}),
      StoreModule.forFeature(RotorTypeDetailsStoreName, rotorTypeDetailsReducer),
      StoreModule.forFeature(RotorTypeListStoreName, rotorTypeListReducer),
      EffectsModule.forRoot([]),
      EffectsModule.forFeature([RotorTypeListEffects]),
      StoreDevtoolsModule.instrument({
        maxAge: 25,
        logOnly: environment.production,
        connectInZone: true,
      }),
      DragDropModule,
      // Rotec-Components Library
      ShellV2Module.forRoot({
        isProduction: isProdMode,
        appLogoAndName: {
          name: 'Rotor Manager',
          logoUrl: './assets/img/rotormanager.png',
        },
        versionInfo,
        msalConfig: environment.msalConfig,
        azureEndpoint: environment.azureEndpoint,
        baseUrl: environment.apiBaseUrl,
        usBaseUrl: environment.usBaseUrl,
      }),
      ActionMenuModule,
      ButtonV2Module,
      ButtonModule,
      CheckboxV2Module,
      ModalModule.forRoot(),
      SideDrawerModule.forRoot(),
      AssetsModule.forRoot(amBaseUrl),
      UnitsModule.forRoot(),
      FileUploadV2Module.forRoot({
        uploadBaseUrl: environment.apiBaseUrl,
        fallbackImgSrcForPipe: './assets/rotor-data-center.svg',
        fallbackImgSrcForFileUpload: './assets/rotor-data-center.svg',
      }),
      PagesV2Module,
      ModalsModule,
      ChipV2Module,
      IconV2Module,
      ToggleV2Module,
      InputV2Module,
      SelectV2Module,
      ReactiveFormsModule,
      RadioV2Module,
      MatRadioModule,
      GridV2Module,
      SpinnerModule.forRoot(),
      OutputV2Module,
      NotificationsV2Module,
      DateV2Module,
      UserRightsModule.forRoot(environment.umBaseUrl),
      PipesModule,
      WorkflowModule
    ),
    ...apiProviders,
    ...serviceProviders,
    ...[
      {
        provide: HTTP_INTERCEPTORS,
        useClass: ErrorInterceptor,
        multi: true,
      },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: ServerErrorInterceptor,
        multi: true,
      },
    ],
    ServerErrorGuard,
    provideAnimations(),
    provideRouter(appRoutes, withHashLocation()),
  ],
}).catch((err) => console.log(err));
